<template>
  <div class="user-list">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="bi bi-people me-2"></i>
            Usuarios
          </h1>
          <p class="page-description">
            Administración de usuarios del sistema
          </p>
        </div>
        <div class="actions">
          <router-link to="/users/create" class="btn btn-primary">
            <i class="bi bi-plus-lg me-2"></i>
            Crear Usuario
          </router-link>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card filters-card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label for="filterEmail" class="form-label">Email</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-envelope"></i>
              </span>
              <input
                type="text"
                class="form-control"
                id="filterEmail"
                v-model="filters.email"
                placeholder="Buscar por email..."
                @input="handleFilterChange"
              >
            </div>
          </div>

          <div class="col-md-3">
            <label for="filterFullname" class="form-label">Nombre</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-person"></i>
              </span>
              <input
                type="text"
                class="form-control"
                id="filterFullname"
                v-model="filters.fullname"
                placeholder="Buscar por nombre..."
                @input="handleFilterChange"
              >
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="includeDeleted"
                v-model="filters.include_deleted"
                @change="handleFilterChange"
              >
              <label class="form-check-label" for="includeDeleted">
                Incluir eliminados
              </label>
            </div>
          </div>

          <div class="col-md-4">
            <div class="d-flex gap-2 justify-content-end">
              <button @click="clearFilters" class="btn btn-outline-secondary">
                <i class="bi bi-x-circle me-1"></i>
                Limpiar Filtros
              </button>
              <button @click="refreshUsers" class="btn btn-primary">
                <i class="bi bi-arrow-clockwise me-1"></i>
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Users Table -->
    <div class="card">
      <div class="card-body">
        <!-- Error State -->
        <div v-if="userStore.error && !userStore.loading" class="alert alert-danger" role="alert">
          <h5 class="alert-heading">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Error al cargar los usuarios
          </h5>
          <p class="mb-2">{{ userStore.error }}</p>
          <hr>
          <div class="d-flex gap-2">
            <button @click="refreshUsers" class="btn btn-sm btn-outline-danger">
              <i class="bi bi-arrow-clockwise me-1"></i>
              Reintentar
            </button>
            <button @click="userStore.clearError" class="btn btn-sm btn-outline-secondary">
              <i class="bi bi-x-circle me-1"></i>
              Cerrar
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="userStore.loading && userStore.users.length === 0" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando usuarios...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="userStore.users.length === 0 && !userStore.error" class="text-center py-5">
          <i class="bi bi-people text-muted" style="font-size: 3rem;"></i>
          <h5 class="mt-3">No se encontraron usuarios</h5>
          <p class="text-muted">
            {{ hasActiveFilters ? 'Intenta cambiar los filtros de búsqueda.' : 'Comienza creando tu primer usuario.' }}
          </p>
          <router-link to="/users/create" class="btn btn-primary mt-2" v-if="!hasActiveFilters">
            <i class="bi bi-plus-lg me-2"></i>
            Crear Primer Usuario
          </router-link>
        </div>

        <!-- Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Cliente</th>
                <th>Token API</th>
                <th>Creado</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in userStore.users" :key="user.id">
                <td>{{ user.id }}</td>
                <td>
                  <strong>{{ user.fullname || '-' }}</strong>
                  <span v-if="user.is_deleted" class="badge bg-danger ms-2">
                    <i class="bi bi-trash me-1"></i>Eliminado
                  </span>
                </td>
                <td>
                  <i class="bi bi-envelope me-1"></i>
                  {{ user.email }}
                </td>
                <td>
                  <span v-if="user.rol_name" class="badge bg-primary">
                    {{ user.rol_name }}
                  </span>
                  <span v-else class="text-muted">Sin rol</span>
                </td>
                <td>
                  <span v-if="user.client_name" class="badge bg-info">
                    {{ user.client_name }}
                  </span>
                  <span v-else class="text-muted">Sin cliente</span>
                </td>
                <td>
                  <span v-if="user.is_token_valid" class="badge bg-success">
                    <i class="bi bi-check-circle me-1"></i>Válido
                  </span>
                  <span v-else class="badge bg-secondary">
                    <i class="bi bi-x-circle me-1"></i>No generado
                  </span>
                  <div v-if="user.token_api_expires" class="small text-muted">
                    Expira: {{ formatDate(user.token_api_expires) }}
                  </div>
                </td>
                <td>
                  <small>{{ formatDate(user.created_at) }}</small>
                </td>
                <td>
                  <span :class="getStatusBadgeClass(user)" class="badge">
                    {{ getStatusText(user) }}
                  </span>
                </td>
                <td>
                  <div class="btn-group btn-group-sm" role="group">
                    <router-link
                      :to="`/users/${user.id}/edit`"
                      class="btn btn-outline-primary"
                      title="Editar"
                      v-if="!user.is_deleted"
                    >
                      <i class="bi bi-pencil"></i>
                    </router-link>

                    <button
                      @click="confirmRestore(user)"
                      class="btn btn-outline-success"
                      title="Restaurar"
                      v-if="user.is_deleted"
                    >
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>

                    <button
                      @click="confirmDelete(user)"
                      class="btn btn-outline-danger"
                      title="Eliminar"
                      v-if="!user.is_deleted"
                    >
                      <i class="bi bi-trash"></i>
                    </button>

                    <button
                      @click="confirmHardDelete(user)"
                      class="btn btn-outline-danger"
                      title="Eliminar permanentemente"
                      v-if="user.is_deleted"
                    >
                      <i class="bi bi-trash-fill"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="userStore.pagination.pages > 1" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted">
            Mostrando {{ (userStore.pagination.page - 1) * userStore.pagination.size + 1 }}
            -
            {{ Math.min(userStore.pagination.page * userStore.pagination.size, userStore.pagination.total) }}
            de {{ userStore.pagination.total }} usuarios
          </div>

          <nav aria-label="Paginación">
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: userStore.pagination.page === 1 }">
                <button class="page-link" @click="changePage(userStore.pagination.page - 1)" :disabled="userStore.pagination.page === 1">
                  <i class="bi bi-chevron-left"></i>
                </button>
              </li>

              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === userStore.pagination.page }"
              >
                <button class="page-link" @click="changePage(page)">
                  {{ page }}
                </button>
              </li>

              <li class="page-item" :class="{ disabled: userStore.pagination.page === userStore.pagination.pages }">
                <button class="page-link" @click="changePage(userStore.pagination.page + 1)" :disabled="userStore.pagination.page === userStore.pagination.pages">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmación Vue Nativo -->
    <Transition name="modal">
      <div v-if="confirmDialog.show" class="modal-overlay" @click="closeConfirmDialog">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="confirmDialog.icon" class="me-2"></i>
              {{ confirmDialog.title }}
            </h5>
            <button type="button" class="btn-close-custom" @click="closeConfirmDialog">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="modal-body">
            {{ confirmDialog.message }}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeConfirmDialog">
              Cancelar
            </button>
            <button
              type="button"
              :class="confirmDialog.buttonClass"
              class="btn"
              @click="executeConfirmAction"
            >
              {{ confirmDialog.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '../../stores/users'

// Store
const userStore = useUserStore()

// Reactive state
const filters = ref({
  email: '',
  fullname: '',
  include_deleted: false
})

// Diálogo de confirmación
const confirmDialog = ref({
  show: false,
  title: '',
  message: '',
  icon: '',
  buttonClass: '',
  confirmText: '',
  action: null,
  userId: null
})

// Computed
const hasActiveFilters = computed(() => {
  return filters.value.email !== '' || filters.value.fullname !== '' || filters.value.include_deleted
})

const visiblePages = computed(() => {
  const current = userStore.pagination.page
  const total = userStore.pagination.pages
  const delta = 2
  const range = []

  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i)
  }

  if (current - delta > 2) {
    range.unshift('...')
  }
  if (current + delta < total - 1) {
    range.push('...')
  }

  range.unshift(1)
  if (total > 1) {
    range.push(total)
  }

  return range.filter((v, i, a) => a.indexOf(v) === i)
})

// Methods
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusBadgeClass = (user) => {
  if (user.is_deleted) {
    return 'bg-danger'
  }
  return 'bg-success'
}

const getStatusText = (user) => {
  if (user.is_deleted) {
    return 'Eliminado'
  }
  return 'Activo'
}

const handleFilterChange = () => {
  userStore.setFilters(filters.value)
  userStore.fetchUsers(1, userStore.pagination.size)
}

const clearFilters = () => {
  filters.value = {
    email: '',
    fullname: '',
    include_deleted: false
  }
  userStore.clearFilters()
  userStore.fetchUsers(1, userStore.pagination.size)
}

const refreshUsers = () => {
  userStore.fetchUsers(userStore.pagination.page, userStore.pagination.size)
}

const changePage = (page) => {
  if (page >= 1 && page <= userStore.pagination.pages) {
    userStore.fetchUsers(page, userStore.pagination.size)
  }
}

// Funciones del diálogo de confirmación
const openConfirmDialog = (config) => {
  confirmDialog.value = {
    show: true,
    ...config
  }
}

const closeConfirmDialog = () => {
  confirmDialog.value = {
    show: false,
    title: '',
    message: '',
    icon: '',
    buttonClass: '',
    confirmText: '',
    action: null,
    userId: null
  }
}

const executeConfirmAction = async () => {
  if (confirmDialog.value.action && confirmDialog.value.userId) {
    try {
      await confirmDialog.value.action(confirmDialog.value.userId)
      closeConfirmDialog()
      await refreshUsers()
    } catch (error) {
      console.error('Error al ejecutar acción:', error)
    }
  }
}

// Funciones de confirmación específicas
const confirmDelete = (user) => {
  openConfirmDialog({
    title: 'Eliminar Usuario',
    message: `¿Estás seguro de que deseas eliminar el usuario "${user.email}"? Esta acción se puede revertir posteriormente.`,
    icon: 'bi bi-trash',
    buttonClass: 'btn-danger',
    confirmText: 'Eliminar',
    action: userStore.deleteUser,
    userId: user.id
  })
}

const confirmRestore = (user) => {
  openConfirmDialog({
    title: 'Restaurar Usuario',
    message: `¿Deseas restaurar el usuario "${user.email}"?`,
    icon: 'bi bi-arrow-counterclockwise',
    buttonClass: 'btn-success',
    confirmText: 'Restaurar',
    action: userStore.restoreUser,
    userId: user.id
  })
}

const confirmHardDelete = (user) => {
  openConfirmDialog({
    title: 'Eliminar Permanentemente',
    message: `¿Estás ABSOLUTAMENTE seguro de que deseas eliminar permanentemente el usuario "${user.email}"? Esta acción NO se puede revertir.`,
    icon: 'bi bi-exclamation-triangle-fill',
    buttonClass: 'btn-danger',
    confirmText: 'Eliminar Permanentemente',
    action: userStore.hardDeleteUser,
    userId: user.id
  })
}

// Lifecycle
onMounted(async () => {
  try {
    // Load initial data
    await userStore.fetchUsers()
  } catch (error) {
    console.error('Error initializing UserList:', error)
  }
})

// Watch for filter changes in store
watch(() => userStore.filters, (newFilters) => {
  filters.value = { ...newFilters }
}, { deep: true })
</script>

<style scoped>
.user-list {
  padding: 0;
}

.page-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.title-section p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary);
}

.filters-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.table {
  margin-bottom: 0;
  color: var(--text-primary);
}

.table thead {
  background: var(--table-header-bg);
  color: var(--text-primary);
}

.table tbody tr:hover {
  background: var(--hover-bg);
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
}

.pagination {
  --bs-pagination-bg: var(--card-bg);
  --bs-pagination-border-color: var(--border-color);
  --bs-pagination-hover-bg: var(--hover-bg);
  --bs-pagination-active-bg: var(--bs-primary);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Modal de confirmación Vue nativo */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid var(--border-color);
  background: #f8f9fa;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
}

.btn-close-custom {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem 0.5rem;
  transition: color 0.2s;
}

.btn-close-custom:hover {
  color: #212529;
}

.modal-body {
  padding: 1.5rem;
  background: white;
  color: #212529;
  font-size: 0.95rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-color);
  background: #f8f9fa;
}

/* Transiciones del modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}

.modal-enter-to .modal-container,
.modal-leave-from .modal-container {
  transform: scale(1);
}
</style>
